import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { OpenAPI } from '../api/core/OpenAPI';
import { request as __request } from '../api/core/request';
import '../../sass/pages/home.scss'
import { fetchDevices } from '../services/deviceService';


interface Environment {
  id: number;
  name: string;
  description: string;
  api_token?: string;
  sso_api_url?: string;
  sso_basic_auth?: string;
  system_username?: string;
  system_password?: string;
  api_username?: string;
  api_password?: string;
}

interface Service {
  id: number;
  name: string;
  description: string;
  base_url: string;
  environment_name: string;
}

interface Endpoint {
  id: number;
  path: string;
  method: string;
  description: string;
  service_name: string;
  requires_authentication: boolean;
  status: number,
  is_active: boolean;
}

interface ServiceDropdownsProps {
  onSelectionChange?: (environment: Environment | null, service: Service | null, endpoint: Endpoint | null) => void;
}

const ServiceDropdowns: React.FC<ServiceDropdownsProps> = ({ onSelectionChange }) => {
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
  
  const [selectedEnvironment, setSelectedEnvironment] = useState<Environment | null>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedEndpoint, setSelectedEndpoint] = useState<Endpoint | null>(null);
  
  const [loading, setLoading] = useState({
    environments: false,
    services: false,
    endpoints: false
  });

  // Load environments on component mount
  useEffect(() => {
    loadEnvironments();
  }, []);

  const loadEnvironments = async () => {
    setLoading(prev => ({ ...prev, environments: true }));
    try {
      const response: any = await __request(OpenAPI, {
        method: "GET",
        url: "/api/apitest/environments/",
      });
      setEnvironments(response);
      
      // Set default environment to env3
      const env3 = response.find((env: Environment) => env.name === 'env3');
      if (env3) {
        setSelectedEnvironment(env3);
        loadServices(env3.id);
        if (onSelectionChange) {
          onSelectionChange(env3, null, null);
        }
      }
    } catch (error) {
      console.error('Error loading environments:', error);
    } finally {
      setLoading(prev => ({ ...prev, environments: false }));
    }
  };

  const loadServices = async (environmentId: number) => {
    setLoading(prev => ({ ...prev, services: true }));
    try {
      const response: any = await __request(OpenAPI, {
        method: "GET",
        url: "/api/apitest/services/",
        query: {
          environment_id: environmentId,
        },
      });
      setServices(response);
    } catch (error) {
      console.error('Error loading services:', error);
      setServices([]);
    } finally {
      setLoading(prev => ({ ...prev, services: false }));
    }
  };

  const loadEndpoints = async (serviceId: number) => {
    setLoading(prev => ({ ...prev, endpoints: true }));
    try {
      const response: any = await __request(OpenAPI, {
        method: "GET",
        url: "/api/apitest/endpoints/",
        query: {
          service_id: serviceId,
        },
      });
      setEndpoints(response);
    } catch (error) {
      console.error('Error loading endpoints:', error);
      setEndpoints([]);
    } finally {
      setLoading(prev => ({ ...prev, endpoints: false }));
    }
  };

  const handleEnvironmentChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const environmentId = parseInt(event.target.value);
    const environment = environments.find(env => env.id === environmentId) || null;
    
    setSelectedEnvironment(environment);
    setSelectedService(null);
    setSelectedEndpoint(null);
    setServices([]);
    setEndpoints([]);
    
    if (environment) {
      loadServices(environment.id);
    }
    
    if (onSelectionChange) {
      onSelectionChange(environment, null, null);
    }
  };

  const handleServiceChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const serviceId = parseInt(event.target.value);
    const service = services.find(svc => svc.id === serviceId) || null;
    
    setSelectedService(service);
    setSelectedEndpoint(null);
    setEndpoints([]);
    
    if (service) {
      loadEndpoints(service.id);
    }
    
    if (onSelectionChange) {
      onSelectionChange(selectedEnvironment, service, null);
    }
  };

  const handleEndpointChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const endpointId = parseInt(event.target.value);
    const endpoint = endpoints.find(ep => ep.id === endpointId) || null;
    
    setSelectedEndpoint(endpoint);
    
    if (onSelectionChange) {
      onSelectionChange(selectedEnvironment, selectedService, endpoint);
    }
  };
  const [sidebarOpen, setSidebarOpen] = useState(true);
   const toggleSidebar = () => setSidebarOpen(prev => !prev);
   const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [query, setQuery] = useState("demo");
  const [devices, setDevices] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());


const activeEndpoints = endpoints.filter(endpoint => endpoint.is_active);
const allSelected = selectedIds.length === activeEndpoints.length && activeEndpoints.length > 0;
const anySelected = selectedIds.length > 0;

const handleSelectAll = () => {
  setSelectedIds(activeEndpoints.map(e => e.id));
};

const handleUnselectAll = () => {
  setSelectedIds([]);
};

const handleCheckboxChange = (id: number) => {
  setSelectedIds(prev =>
    prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
  );
};

const toggleRowExpansion = (endpointId: number) => {
  setExpandedRows(prev => {
    const newSet = new Set(prev);
    if (newSet.has(endpointId)) {
      newSet.delete(endpointId);
    } else {
      newSet.add(endpointId);
    }
    return newSet;
  });
};

const formatJSON = (data: any): string => {
  try {
    return JSON.stringify(data, null, 2);
  } catch (e) {
    return 'Invalid JSON';
  }
};

const decodeJWT = (token: string) => {
  try {
    // JWT tokens have 3 parts separated by dots
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }
    
    // Decode the payload (second part)
    const payload = parts[1];
    // Add padding if needed
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decodedPayload = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));
    
    return JSON.parse(decodedPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

const formatExpirationDate = (exp: number) => {
  try {
    const date = new Date(exp * 1000); // Convert from seconds to milliseconds
    return date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }) + ' ET';
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

const getTokenExpirationInfo = (token: string) => {
  if (!token) return null;
  
  const decoded = decodeJWT(token);
  if (!decoded) return null;
  
  const exp = decoded.exp;
  if (!exp) return null;
  
  const expirationDate = formatExpirationDate(exp);
  const now = Math.floor(Date.now() / 1000);
  const isExpired = exp < now;
  const timeUntilExpiry = exp - now;
  
  return {
    expirationDate,
    isExpired,
    timeUntilExpiry,
    decoded
  };
};

const generateEnvironmentToken = async (environmentId: number) => {
  try {
    const response: any = await __request(OpenAPI, {
      method: "POST",
      url: `/api/apitest/generate-token/?environment_id=${environmentId}`,
    });
    
    // Refresh environments to get updated token
    await loadEnvironments();
    
    return response;
  } catch (error: any) {
    console.error('Error generating token:', error);
    throw error;
  }
};


const handleRunTests = async () => {
  const selectedEndpoints = endpoints.filter(e => selectedIds.includes(e.id));
  console.log("Running tests for:", selectedEndpoints, selectedEnvironment, selectedService);

  setLoading(prev => ({ ...prev, endpoints: true }));
  setError(null);

  try {
    // Run tests for each selected endpoint in parallel
    const responses = await Promise.all(
      selectedEndpoints.map(async (endpoint) => {
        const startTime = performance.now();
        
        try {
          const result = await fetchDevices({
            service: selectedService?.base_url,
            endpoint,
            environmentToken: selectedEnvironment?.api_token
          });
          
          const endTime = performance.now();
          const responseTime = Math.round(endTime - startTime);
          
          // Get first 20 characters of JSON response
          let responsePreview = '';
          try {
            const jsonString = JSON.stringify(result.data);
            responsePreview = jsonString.length > 20 ? jsonString.substring(0, 20) + '...' : jsonString;
          } catch (e) {
            responsePreview = 'Invalid JSON';
          }
          
          return {
            ...endpoint,                    // Preserve original endpoint fields
            testStatus: 'success',          // Test result status
            statusCode: result.status,      // HTTP status code
            responseTime: responseTime,     // Response time in ms
            responsePreview: responsePreview, // First 20 chars of JSON
            data: result.data               // Full response data
          };
        } catch (error: any) {
          const endTime = performance.now();
          const responseTime = Math.round(endTime - startTime);
          
          return {
            ...endpoint,                    // Preserve original endpoint fields
            testStatus: 'error',            // Test result status
            statusCode: error.status || 'N/A', // HTTP status code or N/A
            responseTime: responseTime,     // Response time in ms
            responsePreview: error.message || 'Request failed', // Error message
            data: null                      // No data on error
          };
        }
      })
    );

    console.log("Test results:", responses);
    setDevices(responses); // Store enriched response data
  } catch (err: any) {
    console.error("Error during parallel test execution:", err);
    setError(err.message);
  } finally {
    setLoading(prev => ({ ...prev, endpoints: false }));
  }
};


  return (
    <Row className="main-content-flex">
      
      {/* <h3>Service Selection</h3> */}
       {/* Left Sidebar */}
        <div className={`sidebar left ${sidebarOpen ? "open" : "closed"}`}>
    <Button variant="outline-primary" size="sm" onClick={toggleSidebar} className="toggle-btn">
      {sidebarOpen ? "←" : "→"}
    </Button>

    {sidebarOpen && (
      <>
        <h4>Select Environment</h4>
        <Form.Group className="mb-3">
          <Form.Select value={selectedEnvironment?.id || ''} onChange={handleEnvironmentChange}>
            <option value="">Select Environment</option>
            {environments.map(env => (
              <option key={env.id} value={env.id}>{env.name}</option>
            ))}
          </Form.Select>
        </Form.Group>

        <h4>Select Service</h4>
        <Form.Group className="mb-3">
          <Form.Select value={selectedService?.id || ''} onChange={handleServiceChange}>
            <option value="">Select Service</option>
            {services.map(service => (
              <option key={service.id} value={service.id}>{service.name}</option>
            ))}
          </Form.Select>
        </Form.Group>
        
        {/* Token Generation Section */}
        {selectedEnvironment && (
          <div className="token-generation-section mt-4">
            <h5>Token Management</h5>
            <div className="d-grid gap-2">
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={async () => {
                  try {
                    setLoading(prev => ({ ...prev, environments: true }));
                    await generateEnvironmentToken(selectedEnvironment.id);
                    alert('Token generated successfully!');
                  } catch (error: any) {
                    alert(`Error generating token: ${error.message || 'Unknown error'}`);
                  } finally {
                    setLoading(prev => ({ ...prev, environments: false }));
                  }
                }}
                disabled={loading.environments}
              >
                {loading.environments ? 'Generating...' : 'Generate Token'}
              </Button>
              
              {selectedEnvironment.api_token && (
                <div className="token-info mt-3">
                  <div className="token-status">
                    <small className="text-success">
                      ✅ Token available
                    </small>
                  </div>
                  
                  {(() => {
                    const tokenInfo = getTokenExpirationInfo(selectedEnvironment.api_token);
                    if (!tokenInfo) {
                      return (
                        <div className="mt-2">
                          <small className="text-muted">
                            Unable to decode token expiration
                          </small>
                        </div>
                      );
                    }
                    
                    return (
                      <div className="token-expiration mt-2">
                        <div className={`expiration-status ${tokenInfo.isExpired ? 'text-danger' : 'text-info'}`}>
                          <small>
                            {tokenInfo.isExpired ? '❌ Expired' : '⏰ Expires'} {tokenInfo.expirationDate}
                          </small>
                        </div>
                        {!tokenInfo.isExpired && tokenInfo.timeUntilExpiry > 0 && (
                          <div className="time-remaining mt-1">
                            <small className="text-muted">
                              {tokenInfo.timeUntilExpiry > 3600 
                                ? `${Math.floor(tokenInfo.timeUntilExpiry / 3600)}h ${Math.floor((tokenInfo.timeUntilExpiry % 3600) / 60)}m remaining`
                                : `${Math.floor(tokenInfo.timeUntilExpiry / 60)}m ${tokenInfo.timeUntilExpiry % 60}s remaining`
                              }
                            </small>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        )}
        {/* <div className="sidebar right"> */}
    

  {/* </div> */}
      </>
    )}
  </div>

  {/* Center Section */}
 <div className="center-content">
  <h4>Actions</h4>
  <Form.Group className="mb-3 d-flex flex-row align-items-center gap-3 flex-wrap">
    <Form.Check 
      type="checkbox" 
      label="Select All" 
      checked={allSelected} 
      onChange={handleSelectAll} 
    />
    <Form.Check 
      type="checkbox" 
      label="Unselect All" 
      checked={!anySelected} 
      onChange={handleUnselectAll} 
    />
    <Button 
      variant="success" 
      size="sm" 
      onClick={handleRunTests}
      disabled={!anySelected || loading.endpoints}
    >
      {loading.endpoints ? (
        <>
          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          Running Tests...
        </>
      ) : (
        'Run Tests'
      )}
    </Button>
  </Form.Group>
  
  {error && (
    <div className="alert alert-danger mt-3" role="alert">
      <strong>Error:</strong> {error}
    </div>
  )}

{/* Test Results Summary */}
{devices.length > 0 && (
  <div className="test-summary mt-3 mb-3">
    <h6>Test Summary</h6>
    <div className="d-flex gap-3 flex-wrap">
      <span
        className="badge px-3"
        style={{ backgroundColor: "#007bff", color: "white" }} // Blue
      >
        Total Tests: {devices.length}
      </span>
      <span
        className="badge px-3"
        style={{ backgroundColor: "#2CA057", color: "white" }} // Green
      >
        Passed: {devices.filter(d => d.testStatus === 'success').length}
      </span>
      <span
        className="badge px-3"
        style={{ backgroundColor: "#dc3545", color: "white" }} // Red
      >
        Failed: {devices.filter(d => d.testStatus === 'error').length}
      </span>
      <span
        className="badge px-3"
        style={{ backgroundColor: "#17a2b8", color: "white" }} // Cyan
      >
        Avg Response Time: {Math.round(
          devices.reduce((sum, d) => sum + d.responseTime, 0) / devices.length
        )}ms
      </span>
    </div>
  </div>
)}


  {selectedEnvironment && selectedService  && (
    <div className="selected-info-table">
    {selectedService && ( <h5>Selected {selectedService.name}  Configuration</h5>)} 
      <div className="table-wrapper">
       <table className="config-table">
  <colgroup>
    <col style={{ width: "60px" }} />         {/* Checkbox */}
    {/* <col style={{ width: "140px" }} />        */}
    <col style={{ width: "360px" }} />        {/* Endpoint */}
    <col style={{ width: "160px" }} />        {/* Authentication */}
    <col style={{ width: "160px" }} />        {/* Authentication */}
    <col style={{ width: "120px" }} />        {/* Active */}
  </colgroup>

  <thead>
    <tr>
      <th>Select</th>
      {/* <th>Service</th> */}
      <th>Endpoint</th>
      <th>Test Result</th>
      <th>Response Time</th>
      <th>Response Detail</th>
    </tr>
  </thead>

  <tbody>
    {endpoints
      .filter(endpoint => endpoint.is_active) // Only show active endpoints
      .map(endpoint => {
        // Find corresponding test result
        const testResult = devices.find(device => device.id === endpoint.id);
        
        return (
          <tr key={endpoint.id}>
            <td>
              <input 
                type="checkbox" 
                checked={selectedIds.includes(endpoint.id)}
                onChange={() => handleCheckboxChange(endpoint.id)}
              />
            </td>
            <td title={endpoint.path}>
              {endpoint.method} {endpoint.path}
            </td>
            <td>
              {testResult ? (
                <span className={testResult.testStatus === 'success' ? 'text-success' : 'text-danger'}>
                  {testResult.testStatus === 'success' ? '✅ Success' : '❌ Failed'}
                  {testResult.statusCode && ` (${testResult.statusCode})`}
                </span>
              ) : (
                <span className="text-muted">Not tested</span>
              )}
            </td>
            <td>
              {testResult ? (
                <span>{testResult.responseTime}ms</span>
              ) : (
                <span className="text-muted">-</span>
              )}
            </td>
            <td>
              {testResult ? (
                <div className="json-viewer">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary mb-2"
                    onClick={() => toggleRowExpansion(endpoint.id)}
                  >
                    {expandedRows.has(endpoint.id) ? '▼ Collapse' : '▶ Expand'}
                  </button>
                  {expandedRows.has(endpoint.id) ? (
                    <pre className="json-content bg-light p-2 rounded small" style={{ maxHeight: '300px', overflow: 'auto' }}>
                      {formatJSON(testResult.data)}
                    </pre>
                  ) : (
                    <div className="text-muted small">
                      {testResult.responsePreview}
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-muted">-</span>
              )}
            </td>
          </tr>
        );
      })}
  </tbody>
</table>
      </div>
    </div>
  )}
</div>
  {/* <div className="center-content">
       {selectedEnvironment && selectedService && selectedEndpoint && (
            <div className="selected-info">
              <h5>Selected Configuration</h5>
              <p><strong>Environment:</strong> {selectedEnvironment.name}</p>
              <p><strong>Service:</strong> {selectedService.name}</p>
              <p><strong>Base URL:</strong> {selectedService.base_url}</p>
              <p><strong>Endpoint:</strong> {selectedEndpoint.method} {selectedEndpoint.path}</p>
              <p><strong>Full URL:</strong> {selectedService.base_url}{selectedEndpoint.path}</p>
              {selectedEndpoint.requires_authentication && (
                <p className="text-warning"><strong>⚠️ Requires Authentication</strong></p>
              )}
            </div>
          )}
  </div> */}

  {/* Right Sidebar */}
  


        {/* <Col lg={3} className="sidebar">
          <h4>Select Environment</h4> 
          <Form.Group className="mb-3">
            <Form.Select value={selectedEnvironment?.id || ''} onChange={handleEnvironmentChange}>
              <option value="">Select Environment</option>
              {environments.map(env => (
                <option key={env.id} value={env.id}>{env.name}</option>
              ))}
            </Form.Select>
            {selectedEnvironment && <Form.Text>{selectedEnvironment.description}</Form.Text>}
          </Form.Group>

          <h4>Select Service</h4>
          <Form.Group className="mb-3">
            <Form.Select value={selectedService?.id || ''} onChange={handleServiceChange}>
              <option value="">Select Service</option>
              {services.map(service => (
                <option key={service.id} value={service.id}>{service.name}</option>
              ))}
            </Form.Select>
            {selectedService && <Form.Text>{selectedService.description}</Form.Text>}
          </Form.Group>
        </Col> */}

        {/* Center Section */}
        {/* <Col lg={6} className="center-content">
          {selectedEnvironment && selectedService && selectedEndpoint && (
            <div className="selected-info">
              <h5>Selected Configuration</h5>
              <p><strong>Environment:</strong> {selectedEnvironment.name}</p>
              <p><strong>Service:</strong> {selectedService.name}</p>
              <p><strong>Base URL:</strong> {selectedService.base_url}</p>
              <p><strong>Endpoint:</strong> {selectedEndpoint.method} {selectedEndpoint.path}</p>
              <p><strong>Full URL:</strong> {selectedService.base_url}{selectedEndpoint.path}</p>
              {selectedEndpoint.requires_authentication && (
                <p className="text-warning"><strong>⚠️ Requires Authentication</strong></p>
              )}
            </div>
          )}
        </Col> */}

        {/* Right Sidebar */}
        {/* <Col lg={3} className="sidebar">
          <h4>Select Endpoint</h4>
          <Form.Group className="mb-3">
            <Form.Select value={selectedEndpoint?.id || ''} onChange={handleEndpointChange}>
              <option value="">Select Endpoint</option>
              {endpoints.map(endpoint => (
                <option key={endpoint.id} value={endpoint.id}>
                  {endpoint.method} {endpoint.path}
                </option>
              ))}
            </Form.Select>
            {selectedEndpoint && <Form.Text>{selectedEndpoint.description}</Form.Text>}
          </Form.Group>
        </Col> */}
      
      {/* Environment Dropdown */}
      {/* <Form.Group className="mb-3">
        <Form.Label>Environment *</Form.Label>
        <Form.Select
          value={selectedEnvironment?.id || ''}
          onChange={handleEnvironmentChange}
          disabled={loading.environments}
        >
          <option value="">Select Environment</option>
          {environments.map(env => (
            <option key={env.id} value={env.id}>
              {env.name}
            </option>
          ))}
        </Form.Select>
        {selectedEnvironment && (
          <Form.Text className="text-muted">
            {selectedEnvironment.description}
          </Form.Text>
        )}
      </Form.Group> */}

      {/* Service Dropdown */}
      {/* <Form.Group className="mb-3">
        <Form.Label>Service *</Form.Label>
        <Form.Select
          value={selectedService?.id || ''}
          onChange={handleServiceChange}
          disabled={!selectedEnvironment || loading.services}
        >
          <option value="">Select Service</option>
          {services.map(service => (
            <option key={service.id} value={service.id}>
              {service.name}
            </option>
          ))}
        </Form.Select>
        {selectedService && (
          <Form.Text className="text-muted">
            {selectedService.description}
          </Form.Text>
        )}
      </Form.Group> */}

      {/* Endpoint Dropdown */}
      {/* <Form.Group className="mb-3">
        <Form.Label>Endpoint *</Form.Label>
        <Form.Select
          value={selectedEndpoint?.id || ''}
          onChange={handleEndpointChange}
          disabled={!selectedService || loading.endpoints}
        >
          <option value="">Select Endpoint</option>
          {endpoints.map(endpoint => (
            <option key={endpoint.id} value={endpoint.id}>
              {endpoint.method} {endpoint.path}
            </option>
          ))}
        </Form.Select>
        {selectedEndpoint && (
          <Form.Text className="text-muted">
            {selectedEndpoint.description}
          </Form.Text>
        )}
      </Form.Group> */}

      {/* Display selected information */}
      {/* {selectedEnvironment && selectedService && selectedEndpoint && (
        <div className="mt-4 p-3 bg-light rounded">
          <h5>Selected Configuration:</h5>
          <p><strong>Environment:</strong> {selectedEnvironment.name}</p>
          <p><strong>Service:</strong> {selectedService.name}</p>
          <p><strong>Base URL:</strong> {selectedService.base_url}</p>
          <p><strong>Endpoint:</strong> {selectedEndpoint.method} {selectedEndpoint.path}</p>
          <p><strong>Full URL:</strong> {selectedService.base_url}{selectedEndpoint.path}</p>
          {selectedEndpoint.requires_authentication && (
            <p className="text-warning"><strong>⚠️ Requires Authentication</strong></p>
          )}
        </div>
      )} */}
    </Row>
  );
};

export default ServiceDropdowns; 